import React from 'react';
import BlueCallToAction from '../components/common/BlueCallToAction';

/**
 * IntegrationsPage component
 * Showcases platform integrations and connectivity options
 */
const IntegrationsPage = () => {
  const integrations = [
    {
      category: "Electronic Health Records (EHR)",
      systems: [
        { name: "Epic", description: "Seamless integration with Epic's comprehensive EHR platform" },
        { name: "Cerner", description: "Direct connectivity to Cerner's healthcare information systems" },
        { name: "Allscripts", description: "Integration with Allscripts clinical and financial solutions" },
        { name: "athenahealth", description: "Cloud-based integration with athenahealth services" }
      ],
      icon: "🏥"
    },
    {
      category: "Revenue Cycle Management",
      systems: [
        { name: "Epic Revenue Cycle", description: "Complete integration with Epic's revenue cycle tools" },
        { name: "McKesson RelayHealth", description: "Connectivity to McKesson's financial solutions" },
        { name: "Change Healthcare", description: "Integration with Change Healthcare's revenue cycle platform" },
        { name: "Optum360", description: "Direct connection to Optum's revenue cycle services" }
      ],
      icon: "💰"
    },
    {
      category: "Data Platforms",
      systems: [
        { name: "Snowflake", description: "Native integration with Snowflake data warehouse" },
        { name: "Azure Data Lake", description: "Seamless connectivity to Microsoft Azure cloud services" },
        { name: "AWS HealthLake", description: "Integration with Amazon's healthcare data service" },
        { name: "Google Cloud Healthcare API", description: "Connection to Google's healthcare cloud platform" }
      ],
      icon: "📊"
    },
    {
      category: "Authentication & Security",
      systems: [
        { name: "Microsoft Authenticator", description: "Multi-factor authentication integration" },
        { name: "Azure Active Directory", description: "Enterprise identity and access management" },
        { name: "Okta", description: "Single sign-on and identity management" },
        { name: "Ping Identity", description: "Secure authentication and authorization" }
      ],
      icon: "🔐"
    }
  ];

  const apiFeatures = [
    {
      title: "RESTful APIs",
      description: "Modern REST API endpoints for seamless integration",
      features: ["JSON data format", "OAuth 2.0 authentication", "Rate limiting", "Comprehensive documentation"]
    },
    {
      title: "Real-time Webhooks",
      description: "Instant notifications for critical events and updates",
      features: ["Event-driven architecture", "Secure payload delivery", "Retry mechanisms", "Custom endpoint configuration"]
    },
    {
      title: "FHIR Compliance",
      description: "Full support for Fast Healthcare Interoperability Resources",
      features: ["FHIR R4 standard", "Resource validation", "Bulk data export", "Patient consent management"]
    },
    {
      title: "HL7 Integration",
      description: "Complete HL7 message processing and routing",
      features: ["HL7 v2.x support", "Message transformation", "Routing rules", "Error handling"]
    }
  ];

  return (
    <div className="integrations-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Platform <span className="text-primary-300">Integrations</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              Connect seamlessly with your existing healthcare systems and workflows
              through our comprehensive integration platform.
            </p>
          </div>
        </div>
      </section>

      {/* Integration Categories Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Supported Integrations
          </h2>
          <div className="space-y-12 max-w-6xl mx-auto">
            {integrations.map((category, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4">{category.icon}</div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {category.category}
                  </h3>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  {category.systems.map((system, idx) => (
                    <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                      <h4 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                        {system.name}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-300">
                        {system.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API Features Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            API & Connectivity Features
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {apiFeatures.map((feature, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {feature.description}
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                  {feature.features.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <BlueCallToAction 
        heading="Ready to integrate with your existing systems?"
        description="Our integration specialists can help you connect seamlessly with your current infrastructure."
        primaryButtonText="Schedule Integration Consultation"
        primaryButtonLink="/consultation"
        secondaryButtonText="View API Documentation"
        secondaryButtonLink="/documentation"
      />
    </div>
  );
};

export default IntegrationsPage;